import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createStructuredSelector } from 'reselect';
import { RoomSelectorDesktopEl } from '../RoomSelectorDesktop.style';
import {
    CMS,
    CurrentAccommodationSearchSessionStorage,
    HotelRoomUtils,
    TrackingUtils,
    withPageContext,
} from '../../../utils';
import {
    EntryPoint,
    FieldType,
    HotelStandalonePaxRestrictionPropsDefault,
    RoomsProps,
    TabTypeProp,
    TabTypePropDefault,
} from '../../../models';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { Div } from '../../../layouts';
import {
    getAddRoomSelectorClassNames,
    useRoomSelectorClassNames,
} from '../useRoomSelectorClassNames';
import { addRoom } from '../../../redux/currentSearch/actions';
import ConnectedHSAPaxSelector from './HotelStandalonePaxSelector';
import {
    hasHotelStandaloneKidsErrors,
    getRooms,
    canAddRoom,
} from '../../../redux/currentSearch/hotelStandalone.selectors';
import { getActiveProduct } from '../../../redux/currentSearch/selectors';
import { ButtonDefault } from '../../../components';
import buttonClassNames from '../../SearchButton/buttonClassNames';
import { ErrorMessage } from '../../PaxSelector';
import { useSearchId } from '../../SearchManager/SearchIdProvider';

const FIELD_NAME_TRACKING = 'guest';

const RoomSelector = ({
    activeProduct,
    fieldFocused = false,
    setFieldFocused = Function.prototype,
    rooms,
    hasError,
    showAddRoom,
    addRoom,
    trackEvent,
}) => {
    const { t } = useTranslation();
    const { updateSearchId } = useSearchId();
    const node = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [roomSelectorClass] = useRoomSelectorClassNames(isOpen, hasError);
    const numPax = HotelRoomUtils.getTotalPax(rooms);
    const showErrorMaxNumPax = numPax >= HotelStandalonePaxRestrictionPropsDefault.maxPassengers;
    const onClickAddRoom = () => {
        const newRooms = HotelRoomUtils.getNewRoom(
            rooms,
            HotelStandalonePaxRestrictionPropsDefault,
        );
        addRoom(newRooms);
        CurrentAccommodationSearchSessionStorage.setRooms(newRooms);
        updateSearchId();
    };

    const handleTracking = (label) => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        activeProduct === EntryPoint.HOTELS && trackEvent({ action, label });
    };

    const toggle = () => {
        setFieldFocused(FieldType.ROOM, fieldFocused !== FieldType.ROOM);
        if (!isOpen) {
            handleTracking(TrackingUtils.LABEL.fieldOpen(activeProduct, FIELD_NAME_TRACKING));
        }
        setOpen(!isOpen);
    };
    const close = () => {
        setFieldFocused(null);
        setOpen(false);
    };
    const kidsAgeRange = HotelRoomUtils.getKidsRange(HotelStandalonePaxRestrictionPropsDefault);
    const addRoomClass = getAddRoomSelectorClassNames(false);
    useEffect(() => setOpen(fieldFocused === FieldType.ROOM), [fieldFocused]);
    useEffect(() => {
        const { rooms: currentRooms } = CurrentAccommodationSearchSessionStorage.getAccommodationCurrentSearch();
        if (!currentRooms || !currentRooms.length && rooms?.[0]) {
            CurrentAccommodationSearchSessionStorage.setRooms(rooms[0]);
        }
    }, [rooms]);

    return (
        <div ref={node}>
            <div className={roomSelectorClass.header} onClick={toggle}>
                <Div
                    className="odf-input odf-text-lg odf-input-m odf-input-inline odf-input-inline-last odf-select odf-space-outer-right-m"
                    data-testid="room-pax-selector-summary"
                    whiteSpace="nowrap"
                    style={{ paddingTop: 10 }}
                >
                    <CMS id="dynpack.searchSummary.rooms" values={{ count: rooms.length }} />
                    <div className="odf-icon odf-icon-pax odf-space-inner-left-xs" />
                    <span data-testid="room-selector-number-pax">{numPax}</span>
                </Div>
                <span className={roomSelectorClass.arrow} />
            </div>
            <Dropdown open={isOpen} onClose={close} parentRef={node}>
                <RoomSelectorDesktopEl
                    className={roomSelectorClass.paxSelector}
                    data-testid="room-pax-selector-picker"
                >
                    {rooms.map((room, index) => (
                        <ConnectedHSAPaxSelector
                            room={room}
                            roomNumber={index}
                            key={room.id}
                            numPax={numPax}
                            hasError={hasError}
                            showRoomHeader
                            paxRestrictions={HotelRoomUtils.getRoomPaxRanges(
                                room,
                                HotelStandalonePaxRestrictionPropsDefault,
                                numPax,
                            )}
                            kidsAgeRange={kidsAgeRange}
                        />
                    ))}
                    {showAddRoom && (
                        <div
                            className={addRoomClass.wrapper}
                            onClick={onClickAddRoom}
                            data-testid="add-room-selector"
                        >
                            <span className="odf-icon odf-icon-plus odf-icon-s odf-text-action odf-space-inner-xs" />
                            <span className="odf-link">
                                <CMS id="roomSelector.button.addRoom" />
                            </span>
                        </div>
                    )}
                    {showErrorMaxNumPax && (
                        <div className="odf-space-inner-top-s odf-space-inner-right-m odf-space-inner-left-m">
                            <ErrorMessage
                                messages={[
                                    {
                                        key: 'guestSelector.guests.max',
                                        values: {
                                            number:
                                                HotelStandalonePaxRestrictionPropsDefault.maxPassengers,
                                        },
                                    },
                                ]}
                            />
                        </div>
                    )}
                    <div className="odf-input-group">
                        <div className="odf-space-inner-top-m odf-space-inner-bottom-m">
                            <ButtonDefault
                                className={`odf-col-span12 ${buttonClassNames(
                                    'primary',
                                    false,
                                    false,
                                )}`}
                                onClick={close}
                            >
                                {t('search.button.done')}
                            </ButtonDefault>
                        </div>
                    </div>
                </RoomSelectorDesktopEl>
            </Dropdown>
            {hasError && (
                <span
                    className="odf-label odf-text-sm odf-text-negative odf-space-outer-xs"
                    data-testid="room-selector-children-error"
                >
                    <CMS id="roomSelector.children.error" />
                </span>
            )}
        </div>
    );
};

RoomSelector.propTypes = {
    fieldFocused: PropTypes.string,
    setFieldFocused: PropTypes.func,
    rooms: RoomsProps.isRequired,
    hasError: PropTypes.bool.isRequired,
    showAddRoom: PropTypes.bool.isRequired,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
};

RoomSelector.defaultProps = {
    activeProduct: TabTypePropDefault,
};

const mapStateToPropsSelector = createStructuredSelector({
    activeProduct: getActiveProduct,
    rooms: getRooms,
    hasError: hasHotelStandaloneKidsErrors,
    showAddRoom: canAddRoom,
});

export const HotelStandaloneRoomSelectorDesktop = connect(mapStateToPropsSelector, {
    addRoom,
})(withPageContext(RoomSelector));
